/**
 * Enterprise Protected Route Component
 * Защищает маршруты на основе аутентификации и ролей
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';
import LoadingScreen from '../ui/LoadingScreen';
import { InternalRole, INTERNAL_ROLE_LEVELS, InternalRoleLevel } from '../../types/enums/internal-roles.enum';
import { EventRole } from '../../types/enums/event-roles.enum';
import { logger } from '../../utils/logger';

type UserRole = InternalRole | EventRole | 'admin' | 'manager' | 'moderator' | 'user' | 'guest';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  requiredPermissions?: string[];
  fallbackPath?: string;
}

export default function ProtectedRoute({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  fallbackPath = '/auth/login'
}: ProtectedRouteProps) {
  const { user, isAuthenticated, status, error, permissions: userPermissionsFromAuth, userRoles } = useAuth();
  const { currentRole, hasPermission: _hasPermission } = useRole();
  const location = useLocation();

  // Ждём полной инициализации auth-стека перед любыми проверками доступа
  if (status !== 'ready') {
    return <LoadingScreen message="Проверка доступа..." />;
  }

  // Если есть ошибка аутентификации, показываем её
  if (error) {
    logger.error('ProtectedRoute: Ошибка аутентификации', error);
  }

  // Если не аутентифицирован, перенаправляем на логин
  if (!isAuthenticated || !user) {
    logger.debug('ProtectedRoute: Пользователь не аутентифицирован, перенаправляем на логин');
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location, error }} 
        replace 
      />
    );
  }

  // SUPER_ADMIN и PLATFORM_ADMIN обходят все проверки ролей и permissions
  const currentRoleKey = currentRole?.role;
  const isSuperAdmin =
    currentRoleKey === InternalRole.SUPER_ADMIN ||
    (currentRoleKey != null &&
      currentRoleKey in INTERNAL_ROLE_LEVELS &&
      INTERNAL_ROLE_LEVELS[currentRoleKey as InternalRole] <= InternalRoleLevel.LEVEL_1) ||
    (userRoles?.internalRoles?.some(
      (r) => r.role === InternalRole.SUPER_ADMIN && r.isActive,
    ) ?? false);
  const isPlatformAdmin = currentRoleKey === InternalRole.PLATFORM_ADMIN;
  const isInternalAdmin = isSuperAdmin || isPlatformAdmin;

  // Проверяем роли, если они указаны (внутренние администраторы обходят проверку)
  if (requiredRoles.length > 0 && !isInternalAdmin) {
    const userRole = currentRole?.role as UserRole;
    const hasRequiredRole = requiredRoles.includes(userRole);

    if (!hasRequiredRole) {
      logger.warn('ProtectedRoute: Недостаточно прав доступа', {
        userRole,
        requiredRoles
      });

      return (
        <Navigate
          to="/forbidden"
          state={{
            from: location,
            userRole,
            requiredRoles
          }}
          replace
        />
      );
    }
  }

  if (requiredPermissions.length > 0 && !isSuperAdmin) {
    const userPermissions = userPermissionsFromAuth ?? [];
    const hasRequiredPermissions = requiredPermissions.some(p => userPermissions.includes(p));

    if (!hasRequiredPermissions) {
      logger.warn('ProtectedRoute: Недостаточно permissions', {
        requiredPermissions,
        userPermissions
      });

      return (
        <Navigate
          to="/forbidden"
          state={{
            from: location,
            requiredPermissions,
            userPermissions
          }}
          replace
        />
      );
    }
  }

  return <>{children}</>;
}